import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { BnNgIdleService } from 'bn-ng-idle';
import { ConfirmationService } from 'primeng/api';
import { StorageService } from './services/storage.service';
import { Spinkit, SpinnerVisibilityService } from 'ng-http-loader';
import { Constants } from './models/constants';
import { EventType, InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { Subject, Subscription, take, takeUntil, interval, filter, catchError, switchMap, tap } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();

  title = 'EY Global Tax E-Invoicing Solution';
  loggedIn = false;
  public spinkit = Spinkit;

  displayBasic: boolean = false;
  counter = 0;
  showDropdown = false;
  timerSubscription: Subscription;
  alreadyAcquireToken = false;
  constructor(
    private msalService: MsalService,
    private router: Router,
    private storageService: StorageService,
    private confirmationService: ConfirmationService,
    private bnIdle: BnNgIdleService,
    private msalBroadCastService: MsalBroadcastService,
    private spinner: SpinnerVisibilityService) {
  }

  ngOnInit() {
    this.storageService.loggedIn.next(true);

    // FIX: Restore active account from cache if missing
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0 && !this.msalService.instance.getActiveAccount()) {
      this.msalService.instance.setActiveAccount(accounts[0]);
    }

    let searchParams = window?.location?.search ? new URLSearchParams(window.location.search) : null;
    const groupCodeValue = searchParams ? searchParams.get('groupCode') : null;
    const countryValue = searchParams ? searchParams.get('country') : null;

    if (groupCodeValue && countryValue) {
      localStorage.setItem('src', 'external');
      const groupCode = groupCodeValue ? atob(groupCodeValue) : null;
      const country = countryValue ? atob(countryValue).toUpperCase() : null;
      if (groupCode) {
        this.storageService.setWithDefaultPassphrase('groupCode', groupCode);
      }
      if (country) {
        this.storageService.setWithDefaultPassphrase('Country', country?.toUpperCase());
        this.setPermissionForCountry(country?.toUpperCase());
      }
      this.msalBroadCastService.inProgress$.pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        take(1)).subscribe(() => {
          let msalIdToken = this.storageService.getSessionStorage('msal.idtoken');
          if (!msalIdToken) {
            this.acquireTokenSilent();
          } else {
            this.proceedForLogin(msalIdToken);
          }
        });
    } else {
      this.isLoggedIn();
    }

    this.msalBroadCastService.msalSubject$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any) => {
          switch (result.eventType) {
            case EventType.INITIALIZE_START:
              this.spinner.show();
              break;
            case EventType.LOGIN_SUCCESS:
            case EventType.ACQUIRE_TOKEN_SUCCESS:
              if (result?.payload?.account?.username) {
                this.msalService.instance.setActiveAccount(result?.payload?.account);
              }
              if (result?.payload?.idToken) {
                if (localStorage.getItem('src') === 'external') {
                  this.setPermissionForCountry(this.storageService.getWithDefaultPassphrase("Country"));
                }
                this.storageService.setCookies(result?.payload?.idTokenClaims?.nonce, 'nonce');
                if (!this.alreadyAcquireToken) {
                  this.proceedForLogin(result?.payload?.idToken);
                }
                break;
              } else {
                for (let i = 0; i < sessionStorage.length; i++) {
                  const key = sessionStorage.key(i);
                  if (key[0].includes('-idtoken-')) {
                    const idToken = JSON.parse(sessionStorage.getItem(key))?.secret;
                    this.proceedForLogin(idToken);
                    break;
                  }
                }
              }
              break;
            case EventType.LOGIN_FAILURE:
              this.spinner.hide();
              this.router.navigate(['/']);
              this.loggedIn = false;
              break;
            case EventType.HANDLE_REDIRECT_END:
              this.spinner.hide();
              break;
          }
        },
        error: (error) => {
          this.spinner.hide();
          console.log('error', error);
        },
        complete: () => {
          this.spinner.hide();
        }
      });

    this.storageService.concurrFound$
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          this.concurrentPopup();
        }
      });
  }

  acquireTokenSilent() {
    this.spinner.show();
    // FIX: Ensure we always have an active account if cached
    const accounts = this.msalService.instance.getAllAccounts();
    if (!this.msalService.instance.getActiveAccount() && accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
    }

    const activeAccount = this.msalService.instance.getActiveAccount();
    const msalIdToken = this.storageService.getSessionStorage('msal.idtoken');

    if (!activeAccount && !msalIdToken) {
      this.spinner.hide();
      this.msalService.loginPopup();
      return;
    }
    this.msalService.acquireTokenSilent({
      scopes: Constants.MSAL_SILENT_SCOPE,
      account: this.msalService.instance.getActiveAccount()
    }).subscribe({
      next: (result) => {
        if (!this.alreadyAcquireToken) {
          this.proceedForLogin(result.idToken);
        }
      },
      error: (error) => {
        console.log('Silent token error:', error);
        if (error instanceof InteractionRequiredAuthError) {
          this.msalService.loginRedirect();
        }
      }
    });
  }

  setPermissionForCountry(country) {
    let perm = Constants.countryPermList.find(x => x.ctyCode === country).isPerm;
    this.storageService.setWithDefaultPassphrase("Permission", perm);
  }

  proceedForLogin(idToken: any) {
    this.storageService.setSessionStorage('msal.idtoken', idToken);
    this.storageService.setCookies(this.storageService?.createTokenForOneHour()?.getTime()?.toString(), 'msal.idTokenExpiry');
    this.checkIdleUsers();
    this.loggedIn = true;
    this.storageService.loggedIn.next(true);
    this.alreadyAcquireToken = true;
    this.storageService.setTokenInBackend().pipe(
      catchError(err => {
        console.error('Error setting token in backend:', err);
        return this.storageService.endPreviousSession().pipe(
          switchMap(() => this.storageService.setTokenInBackend())
        );
      }),
      tap(() => this.navigateToApplication())
    ).subscribe({
      error: (finalErr) => console.error('Final failure:', finalErr)
    });
  }

  isLoggedIn() {
    this.spinner.show();
    const idToken = this.storageService.getSessionStorage('msal.idtoken');
    if (idToken) {
      this.loggedIn = this.storageService.tokenValidateTime();
      if (this.loggedIn) {
        this.storageService.loggedIn.next(true);
        this.checkIdleUsers();
        this.navigateToApplication();
      } else {
        if (localStorage.getItem('src') === 'external') {
          this.storageService.loggedIn.next(true);
        } else {
          this.storageService.loggedIn.next(false);
        }
        this.storageService.logOut(false);
      }
    } else {
      if (localStorage.getItem('src') === 'external') {
        this.storageService.loggedIn.next(true);
      } else {
        this.storageService.loggedIn.next(false);
        this.loggedIn = false;
      }
    }
    this.spinner.hide();
  }

  private navigateToApplication() {
    this.spinner.hide();

    let currentPath = window.location.pathname;
    const isExternal = localStorage.getItem('src') === 'external';
    if (isExternal) {
      this.alreadyAcquireToken = true;
      if (currentPath.includes('/home/')) {
        this.router.navigate([currentPath]);
      } else {
        this.router.navigate(['/home']);
      }
      return;
    } else {
      if (currentPath !== '/prelogin' && currentPath !== '/') {
        this.router.navigate([currentPath]);
      } else {
        this.router.navigate(['prelogin']);
      }
    }
  }

  showLogout() {
    this.confirmationService.confirm({
      message: 'Unsaved changes will be deleted. Do you want to continue?',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      rejectVisible: true,
      accept: () => {
        this.showDropdown = this.showDropdown === false ? true : false;
        this.confirmationService.close();
      },
      reject: () => {
        return;
      }
    });
  }

  logout(concurrent: boolean) {
    this.storageService.logOut(concurrent);
  }

  checkIdleUsers() {
    this.unsubscribeTimer();
    this.bnIdle.startWatching(Constants.INACTIVITY_TIMEOUT_IN_MINS * 60)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isTimedOut: boolean) => {
        if (isTimedOut) {
          this.displayBasic = true;
          this.startCountdown(60);
        }
      });
  }

  startCountdown(seconds: number) {
    this.unsubscribeTimer();

    const intervalSecond = 1000;
    const stream$ = interval(intervalSecond).pipe(take(seconds));

    this.timerSubscription = stream$.subscribe({
      next: (value) => {
        this.counter = seconds - value;
      },
      error: (err) => {
        console.error(err);
      },
      complete: () => {
        this.onCountdownComplete();
      }
    });
  }

  onCountdownComplete() {
    this.displayBasic = false;
    this.storageService.unAuthUserFound();
  }

  continue() {
    this.unsubscribeTimer();
    this.displayBasic = false;
    this.bnIdle.resetTimer();
  }

  cancel() {
    this.unsubscribeTimer();
    this.onCountdownComplete();
  }

  private unsubscribeTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  concurrentPopup() {
    this.confirmationService.confirm({
      message: 'Concurrent session found. Would you like to logout the existing session & continue?', header: 'Confirmation',
      acceptIcon: "none", rejectIcon: "none",
      accept: () => {
        this.storageService.endPreviousSession().subscribe({
          next: () => {
            this.storageService.triggerRetry();
          },
          error: (err) => {
            console.error('Error ending previous session:', err);
          }
        });
      },
      reject: () => {
        this.logout(true);
      }
    });
  }
}
